import { Box, Stack } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { allProductsOfTypeSelector } from "@store/plan-creation/plan-creation-selectors";
import { useMemo, useState } from "react";
import {
  is2026SphereXgProductIdentifier,
  isCreditProductIdentifier,
  isHoloBuilderLegacyPlanIdentifier,
  isProductAllowedBundle,
  isSphereXgProductIdentifier,
} from "@utils/type-guards";
import { CREDITS_DISPLAY_NAME } from "@utils/limit-data-display";
import {
  PlanCreationModeProps,
  SubjectTypeProps,
} from "@custom-types/base-types";
import {
  plansCreationStateSelector,
  setSelectedProduct,
} from "@store/plan-creation/plan-creation-slice";
import {
  Button,
  Dropdown,
  DropdownOption,
  HolobuilderIcon,
  SphereXGIcon,
  Tooltip,
  IconProps,
} from "@flat-ui";
import { useGenerateRoutes } from "@router/router-helper";
import { capitalize } from "es-toolkit";
import { BaseProduct } from "@utils/product/product-utils-types";
import { BundleFeaturesDialog } from "@pages/create-plan/create-plan-components/bundle-features-dialog";
import { PLAN_APP_NAMES, PlanApp } from "@utils/product/plan-apps";

/** The flat-ui logo of an application */
interface PlanAppIcon {
  /** Renders the logo */
  Icon: (props: IconProps) => React.JSX.Element;

  /** Crops the asset, for artwork that does not fill its own viewBox */
  viewBox?: string;
}

const PLAN_APP_ICONS: Record<PlanApp, PlanAppIcon> = {
  holoBuilder: { Icon: HolobuilderIcon },
  // The artwork covers only the middle 62% x 81% of its 432 square, so it is cropped to fit
  sphereXg: { Icon: SphereXGIcon, viewBox: "82 42 270 349" },
};

/** A group of products shown under a common, non-selectable header in the dropdown */
interface ProductSection {
  /** Unique key of the section, also used as the value of its header option */
  key: string;

  /** The header shown above the products. When omitted the products are listed without a header */
  label?: string;

  /** The products offered in this section */
  products: BaseProduct[];
}

/**
 * The name of a product, followed by the logo of each application it opens so that the user
 * sees which application a plan grants access to before assigning it.
 */
function productLabel(product: BaseProduct): React.ReactNode {
  if (product.apps.length === 0) {
    return product.name;
  }

  return (
    <Stack
      component="span"
      sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
    >
      {product.name}
      {product.apps.map((app) => {
        const { Icon, viewBox } = PLAN_APP_ICONS[app];
        return (
          <Tooltip key={app} title={PLAN_APP_NAMES[app]}>
            <Icon
              viewBox={viewBox}
              // Named here instead of with titleAccess, which would add a second, native tooltip
              role="img"
              aria-label={PLAN_APP_NAMES[app]}
              aria-hidden={false}
              // The Sphere XG logo hardcodes a white fill, which is invisible on the light menu
              sx={{ fontSize: "1.5rem", path: { fill: "currentColor" } }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/**
 * Builds the dropdown options of a section, skipping sections that have no product.
 *
 * When `onShowBundleFeatures` is given, every bundle gets an extra row that opens the
 * list of features it is made of, since its name alone does not say what it grants.
 */
function buildSectionOptions(
  { key, label, products }: ProductSection,
  onShowBundleFeatures?: (bundle: BaseProduct) => void
): DropdownOption[] {
  if (products.length === 0) {
    return [];
  }

  return [
    ...(label ? [{ key, label, value: key, isHeader: true }] : []),
    ...products.map((product) => ({
      key: product.identifier,
      label: productLabel(product),
      value: product.identifier,
      secondaryText:
        onShowBundleFeatures && isProductAllowedBundle(product.identifier) ? (
          <>
            {product.description}
            <BundleFeaturesLink onClick={() => onShowBundleFeatures(product)} />
          </>
        ) : (
          product.description
        ),
    })),
  ];
}

/**
 * The row of a bundle option that opens its feature list. It lives inside a menu item, so
 * every event it handles has to be kept from reaching the item and selecting the bundle.
 */
function BundleFeaturesLink({
  onClick,
}: {
  /** Function called when the user asks to see the features of the bundle */
  onClick(): void;
}): React.JSX.Element {
  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      sx={{
        display: "block",
        width: "fit-content",
        textDecoration: "underline",
        cursor: "pointer",
        "&:hover": { textDecorationThickness: "2px" },
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }
      }}
    >
      This is a bundle, see included features
    </Box>
  );
}

/** Sorts products by their display name, so that they are easy to find in a long list */
function byName(first: BaseProduct, second: BaseProduct): number {
  return (first.name ?? "").localeCompare(second.name ?? "");
}

interface Props extends SubjectTypeProps, PlanCreationModeProps {}

/** A dropdown to select a product */
export function ProductDropdown({
  subjectType,
  creationMode,
}: Props): React.JSX.Element {
  const { selectedProduct, copiedPlan } = useAppSelector(
    plansCreationStateSelector
  );
  const dispatch = useAppDispatch();
  const { generateAboutProductsRoute } = useGenerateRoutes();
  const [bundleToInspect, setBundleToInspect] = useState<BaseProduct | null>(
    null
  );

  const features = useAppSelector(
    allProductsOfTypeSelector({
      productType: "feature",
      subjectType,
    })
  );
  const bundles = useAppSelector(
    allProductsOfTypeSelector({
      productType: "bundle",
      subjectType,
    })
  );
  const customProducts = useAppSelector(
    allProductsOfTypeSelector({
      productType: "custom",
      subjectType,
    })
  );

  const trialProducts = useAppSelector(
    allProductsOfTypeSelector({
      productType: "trial",
      subjectType,
    })
  );

  const sphereXG2026Products = useMemo(
    () =>
      customProducts.filter((product) =>
        is2026SphereXgProductIdentifier(product.identifier)
      ),
    [customProducts]
  );

  const sphereXG2025Products = useMemo(
    () =>
      customProducts.filter(
        (product) =>
          isSphereXgProductIdentifier(product.identifier) &&
          !is2026SphereXgProductIdentifier(product.identifier)
      ),
    [customProducts]
  );

  const sphereXGCredits = useMemo(
    () =>
      customProducts.filter((product) =>
        isCreditProductIdentifier(product.identifier)
      ),
    [customProducts]
  );

  const holoBuilderLegacyPlans = useMemo(
    () =>
      bundles.filter((product) =>
        isHoloBuilderLegacyPlanIdentifier(product.identifier)
      ),
    [bundles]
  );

  /**
   * Everything that is offered as an add-on: the individual features together with the
   * bundles, minus the legacy HoloBuilder plans that are offered as subscriptions instead.
   */
  const addOnProducts = useMemo(
    () =>
      [
        ...bundles.filter(
          (product) => !isHoloBuilderLegacyPlanIdentifier(product.identifier)
        ),
        ...features,
      ].sort(byName),
    [bundles, features]
  );

  const sections = useMemo<ProductSection[]>(() => {
    switch (creationMode) {
      case "subscription": {
        return [
          {
            key: "sphere-xg-plans-2026",
            label: "Sphere XG Plans 2026",
            products: sphereXG2026Products,
          },
          {
            key: "sphere-xg-plans-2025",
            label: "Sphere XG Plans 2025",
            products: sphereXG2025Products,
          },
          {
            key: "sphere-xg-trial-plans",
            label: "Sphere XG Trial Plans",
            products: trialProducts,
          },
          {
            key: "holobuilder-legacy-plans",
            label: "HoloBuilder Legacy Plans",
            products: holoBuilderLegacyPlans,
          },
          {
            key: "tokens",
            label: capitalize(CREDITS_DISPLAY_NAME),
            products: sphereXGCredits,
          },
        ];
      }

      case "feature": {
        // Features and bundles are no longer split up, they form a single sorted list
        return [{ key: "features-and-bundles", products: addOnProducts }];
      }

      case "hardware-license": {
        return [
          {
            key: "bundles",
            label: "Available License types",
            products: bundles,
          },
          { key: "features", label: "Features", products: features },
        ];
      }
    }
  }, [
    creationMode,
    sphereXG2026Products,
    sphereXG2025Products,
    trialProducts,
    holoBuilderLegacyPlans,
    sphereXGCredits,
    addOnProducts,
    bundles,
    features,
  ]);

  /** Every product the user can pick in the current mode, used to resolve the selected value */
  const selectableProducts = useMemo(
    () => sections.flatMap((section) => section.products),
    [sections]
  );

  /**
   * The product of a copied plan is always shown, even when it is not offered to the user anymore,
   * e.g. because it is only visible while testing beta features.
   */
  const shownSections = useMemo<ProductSection[]>(() => {
    if (
      !copiedPlan ||
      !selectedProduct ||
      selectableProducts.includes(selectedProduct)
    ) {
      return sections;
    }

    return [
      ...sections,
      { key: "copied-plan", label: "Copied Plan", products: [selectedProduct] },
    ];
  }, [copiedPlan, sections, selectableProducts, selectedProduct]);

  return (
    <>
      <Stack sx={{ flexDirection: "row" }}>
        <Dropdown
          label={subjectType === "DEVICE" ? "License Type" : "Product"}
          value={selectedProduct?.identifier ?? ""}
          fullWidth
          containerSx={{ flexGrow: 1 }}
          tag="required"
          // A copy is always made of the same product as the copied plan
          disabled={copiedPlan !== null}
          onChange={(event) => {
            const product = selectableProducts.find(
              (product) => product.identifier === event.target.value
            );
            if (product) {
              dispatch(setSelectedProduct(product));
            }
          }}
          options={shownSections.flatMap((section) =>
            buildSectionOptions(
              section,
              // Only the feature page mixes bundles with single features, so only there
              // does the user need to be told which option is a bundle
              creationMode === "feature" ? setBundleToInspect : undefined
            )
          )}
        />
      </Stack>

      {bundleToInspect && (
        <BundleFeaturesDialog
          bundle={bundleToInspect}
          onClose={() => setBundleToInspect(null)}
        />
      )}

      <Button
        onClick={() => {
          const { pathname, search } = generateAboutProductsRoute();
          window.open(`${pathname}${search}`, "_blank");
        }}
        variant="ghost"
        sx={{ px: 0, mt: 1 }}
      >
        Which features are included in the plans?
      </Button>
    </>
  );
}
